/**
 * Контакты фаундеров и сборка черновика обращения для Telegram.
 *
 * Единственный источник правды: карточки в модалке обращения берут отсюда и
 * хендлы, и фото, и текст черновика. Вынесено из CreateTicketModal.vue, потому
 * что сборка текста — чистая функция с десятком ветвлений по пустым полям, и
 * тестировать её отдельно от компонента дешевле и надёжнее.
 *
 * Спека: docs/superpowers/specs/2026-07-25-support-founders-contact-beta-design.md
 */
import dmitryAvatar from '@/assets/images/founders/dmitry.jpg';
import maximAvatar from '@/assets/images/founders/maxim.jpg';

export interface Founder {
    /** Имя, как показываем на карточке. */
    name: string;
    /** Хендл хранится с ведущим `@` — так его видит пользователь. */
    handle: string;
    /** URL фото, подставляется сборщиком. */
    avatar: string;
}

export const FOUNDERS: readonly Founder[] = [
    { name: 'Дмитрий', handle: '@logora_dmitry', avatar: dmitryAvatar },
    { name: 'Максим', handle: '@logora_maxim', avatar: maximAvatar },
];

/**
 * Telegram обрезает черновик по `message_length_max` кодовых точек
 * (core.telegram.org/api/links). Режем сами, чтобы контролировать границу.
 */
const MESSAGE_MAX_LENGTH = 4096;

/** Идентификатор объекта приходит из URL без валидации — не даём ему съесть черновик. */
const CONTEXT_ID_MAX_LENGTH = 128;

/**
 * Управляющие и невидимые символы: C0/C1, zero-width, bidi-переопределения.
 * Черновик — построчный формат, который фаундер читает глазами, поэтому любое
 * подставляемое значение не должно уметь создать новую строку или развернуть
 * текст задом наперёд.
 */
// Два коротких регэкспа вместо одного длинного намеренно: prettier переносит
// длинную строку, и eslint-disable-next-line начинает указывать в пустоту.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g;
const INVISIBLE_CHARS = /[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069]/g;

/** Приводит произвольное значение к однострочному тексту; пустое → ''. */
const sanitize = (value?: string | null): string =>
    typeof value === 'string'
        ? value
              .replace(CONTROL_CHARS, ' ')
              .replace(INVISIBLE_CHARS, ' ')
              .replace(/\s+/g, ' ')
              .trim()
        : '';

/** vue-router на повторяющемся query-параметре отдаёт массив. */
export const firstValue = (value?: string | string[] | null): string | null => {
    if (Array.isArray(value)) return value[0] ?? null;
    return value ?? null;
};

export interface FounderMessageInput {
    firstName?: string | null;
    lastName?: string | null;
    userId?: string | null;
    /** Человекочитаемый label категории, не машинное значение. */
    categoryLabel?: string | null;
    /** Человекочитаемый label типа связанного объекта. */
    contextTypeLabel?: string | null;
    contextId?: string | string[] | null;
}

/**
 * Собирает черновик. Каждая строка независима: отсутствие любого поля просто
 * убирает свою строку, не ломая остальные и не оставляя `undefined` и пустых
 * скобок. Основной путь — клик по фаундеру до выбора категории, поэтому «без
 * категории и без контекста» здесь не краевой случай, а норма.
 */
export function buildFounderMessage(input: FounderMessageInput): string {
    const fullName = [input.firstName, input.lastName].map(sanitize).filter(Boolean).join(' ');
    const userId = sanitize(input.userId);
    const categoryLabel = sanitize(input.categoryLabel);
    const contextTypeLabel = sanitize(input.contextTypeLabel);
    const contextId = sanitize(firstValue(input.contextId)).slice(0, CONTEXT_ID_MAX_LENGTH);

    const lines: string[] = [];

    if (fullName && userId) {
        lines.push(`Меня зовут ${fullName}, мой ID на платформе: ${userId}`);
    } else if (fullName) {
        lines.push(`Меня зовут ${fullName}`);
    } else if (userId) {
        lines.push(`Мой ID на платформе: ${userId}`);
    }

    if (categoryLabel) {
        lines.push(`Категория: ${categoryLabel}`);
    }

    if (contextTypeLabel) {
        lines.push(
            contextId
                ? `Связано с: ${contextTypeLabel} (ID ${contextId})`
                : `Связано с: ${contextTypeLabel}`,
        );
    }

    // Пустой разделитель нужен только когда сверху что-то есть — иначе черновик
    // начался бы с переноса строки.
    if (lines.length > 0) {
        lines.push('');
    }
    lines.push('Создаю обращение:');

    return lines.join('\n');
}

/**
 * Собирает ссылку на личный чат с предзаполненным черновиком.
 *
 * Порядок операций жёсткий и обоснован документацией Telegram:
 *   1. срезать ведущий `@` — иначе t.me отдаёт 302 на telegram.org, и вместо
 *      чата пользователь попадает на маркетинговую главную;
 *   2. защитить ведущий `@` в тексте пробелом — иначе клиент примет черновик за
 *      inline-запрос. Только на сырой строке: после кодирования `@` уже `%40`;
 *   3. резать СЫРОЙ текст и по кодовым точкам — срез по UTF-16 рвёт суррогатную
 *      пару, а срез уже закодированной строки рвёт процент-последовательность, и
 *      Telegram молча выбрасывает весь параметр `text`;
 *   4. кодировать последним шагом и строкой, а не через URLSearchParams — тот
 *      кодирует пробел как `+`.
 */
export function buildFounderTelegramUrl(handle: string, message: string): string {
    const username = String(handle ?? '')
        .trim()
        .replace(/^@+/, '');
    const chatUrl = `https://t.me/${username}`;

    try {
        const guarded = message.startsWith('@') ? ` ${message}` : message;
        const points = Array.from(guarded);
        const capped =
            points.length > MESSAGE_MAX_LENGTH
                ? points.slice(0, MESSAGE_MAX_LENGTH).join('')
                : guarded;

        return `${chatUrl}?text=${encodeURIComponent(capped)}`;
    } catch {
        // Черновик — приятный бонус, а не условие работы ссылки: при любой
        // проблеме с кодированием отдаём чистый чат, но не роняем рендер шага.
        return chatUrl;
    }
}
